import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { StatusPill } from "@/components/StatusBanner";
import { WidgetError } from "@/components/WidgetError";
import { fmtDate } from "@/lib/format";
import { Truck } from "lucide-react";

export default function TruckstopDashboard() {
  const { user, tenant } = useAuth();
  const [openCountState, setOpenCountState] = useState({ status: "loading", value: 0, error: null });
  const [recentState, setRecentState] = useState({ status: "loading", items: [], error: null });

  const loadOpenCount = useCallback(async () => {
    setOpenCountState((s) => ({ ...s, status: "loading", error: null }));
    try {
      const { data } = await api.get("/orders", { params: { status: "open", limit: 1 } });
      setOpenCountState({ status: "ok", value: data.total ?? 0, error: null });
    } catch (err) {
      setOpenCountState({ status: "error", value: 0, error: err });
    }
  }, []);

  const loadRecent = useCallback(async () => {
    setRecentState((s) => ({ ...s, status: "loading", error: null }));
    try {
      const { data } = await api.get("/orders", { params: { limit: 6 } });
      setRecentState({ status: "ok", items: data.items || [], error: null });
    } catch (err) {
      setRecentState({ status: "error", items: [], error: err });
    }
  }, []);

  useEffect(() => {
    // Independent loads — one failure must not sink the other.
    loadOpenCount();
    loadRecent();
  }, [loadOpenCount, loadRecent]);

  return (
    <div>
      <div className="mb-6 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="label-overline">Welcome</div>
          <h1 className="text-3xl font-black tracking-tight mt-1">{tenant?.business_name || "Dashboard"}</h1>
          <p className="text-slate-600 mt-1 text-sm">Fast-lane view of your yard.</p>
        </div>
        <Link to="/app/new-entry">
          <Button data-testid="dashboard-new-entry-cta"
                  className="rounded bg-blue-700 hover:bg-blue-700 h-12 px-6 text-base">
            <Truck className="mr-2" size={18}/> New Truck Entry
          </Button>
        </Link>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white border border-slate-200 p-5">
          <div className="label-overline">Open Orders</div>
          {openCountState.status === "error" ? (
            <div className="mt-2">
              <WidgetError
                error={openCountState.error}
                onRetry={loadOpenCount}
                title="Could not load open orders"
                testId="dashboard-open-count-error"
              />
            </div>
          ) : (
            <>
              <div className="mt-2 text-4xl font-black font-mono" data-testid="dashboard-open-count">
                {openCountState.status === "loading" ? "…" : openCountState.value}
              </div>
              <Link to="/app/open-orders" className="text-blue-700 font-semibold text-sm mt-3 inline-block">
                View queue →
              </Link>
            </>
          )}
        </div>
        <div className="bg-white border border-slate-200 p-5">
          <div className="label-overline">Signed in as</div>
          <div className="mt-2 text-sm font-mono">{user?.email}</div>
          <div className="text-xs text-slate-500 mt-1">{user?.role}</div>
        </div>
        <div className="bg-white border border-slate-200 p-5">
          <div className="label-overline">Tenant status</div>
          <div className="mt-3">{tenant && <StatusPill status={tenant.status} />}</div>
        </div>
      </div>

      <div className="mt-6 bg-white border border-slate-200">
        <div className="px-4 py-3 border-b border-slate-200 label-overline flex items-center justify-between">
          <span>Recent activity</span>
          {recentState.status === "error" && (
            <button
              data-testid="dashboard-recent-retry"
              onClick={loadRecent}
              className="text-xs text-blue-700 hover:underline"
            >
              Retry
            </button>
          )}
        </div>
        {recentState.status === "error" ? (
          <div className="p-4">
            <WidgetError
              error={recentState.error}
              onRetry={loadRecent}
              title="Could not load recent activity"
              testId="dashboard-recent-error"
            />
          </div>
        ) : (
          <>
            {/* Mobile: stacked cards */}
            <div className="sm:hidden divide-y divide-slate-100">
              {recentState.status === "loading" && (
                <div className="px-4 py-6 text-center text-slate-500">Loading…</div>
              )}
              {recentState.status === "ok" && recentState.items.length === 0 && (
                <div className="px-4 py-6 text-center text-slate-500">No orders yet — start with a New Truck Entry.</div>
              )}
              {recentState.status === "ok" && recentState.items.map((o) => (
                <Link key={o.id} to={`/app/orders/${o.id}`}
                      className="block px-4 py-3 active:bg-slate-50">
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-mono text-xs text-blue-700 shrink-0">{o.order_no}</div>
                    <StatusPill status={o.status} />
                  </div>
                  <div className="mt-1 font-mono font-bold text-blue-700 truncate">{o.registration_no}</div>
                  <div className="mt-0.5 text-xs text-slate-600 truncate">{o.service_type_snapshot?.label}</div>
                  <div className="mt-1 font-mono text-[10px] text-slate-500">{fmtDate(o.opened_at)}</div>
                </Link>
              ))}
            </div>
            {/* Tablet+ : table (contained horizontal scroll) */}
            <div className="hidden sm:block overflow-x-auto">
              <table className="tbl w-full text-sm min-w-[640px]">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr className="label-overline text-slate-600">
                    <th className="text-left px-4 py-2">Order</th>
                    <th className="text-left px-4 py-2">Registration</th>
                    <th className="text-left px-4 py-2">Service</th>
                    <th className="text-left px-4 py-2">Status</th>
                    <th className="text-left px-4 py-2">Opened</th>
                  </tr>
                </thead>
                <tbody>
                  {recentState.status === "loading" && (
                    <tr><td colSpan={5} className="px-4 py-6 text-center text-slate-500">Loading…</td></tr>
                  )}
                  {recentState.status === "ok" && recentState.items.length === 0 && (
                    <tr><td colSpan={5} className="px-4 py-6 text-center text-slate-500">No orders yet — start with a New Truck Entry.</td></tr>
                  )}
                  {recentState.status === "ok" && recentState.items.map((o) => (
                    <tr key={o.id} className="border-b border-slate-100">
                      <td className="px-4 py-2 font-mono">
                        <Link to={`/app/orders/${o.id}`} className="text-blue-700 hover:underline">{o.order_no}</Link>
                      </td>
                      <td className="px-4 py-2 font-mono font-bold">{o.registration_no}</td>
                      <td className="px-4 py-2">{o.service_type_snapshot?.label}</td>
                      <td className="px-4 py-2"><StatusPill status={o.status} /></td>
                      <td className="px-4 py-2 font-mono text-xs text-slate-500 whitespace-nowrap">{fmtDate(o.opened_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
